<script setup>
// Meta Ads bolumlerinin ortak kabugu: baslik, hesap secici ve ust sekmeler.
//
// Kenar cubugunda yalnizca alti ust bolum gorunur (Genel Bakis, Reklamlar,
// Kutuphane, Hedef Kitleler, Istatistikler, Ayarlar); alt bolumler buradaki
// sekmelerden acilir. SaaS'ta menuyu sisirmemek icin bu yapi secildi.
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  aracCalistir,
  diziyeCevir,
  seciliHesabiOku,
  seciliHesabiYaz,
} from './kopru.js';

const props = defineProps({
  // [{ ad: 'Kampanyalar', rota: 'meta_ads_kampanyalar' }, ...]
  sekmeler: { type: Array, default: () => [] },
  baslik: { type: String, required: true },
  // Hesap secici gerekmeyen bolumler (or. Ayarlar > Baglantilar) icin
  hesapGerekli: { type: Boolean, default: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const hesaplar = ref([]);
const seciliHesap = ref(seciliHesabiOku());
const yukleniyor = ref(false);
const hata = ref('');

const accountId = computed(() => route.params.accountId);

const hesapAyrinti = computed(() =>
  hesaplar.value.find(h => h.id === seciliHesap.value || h.account_id === seciliHesap.value)
);

const hesaplariYukle = async () => {
  if (!props.hesapGerekli) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_ad_accounts');
    hesaplar.value = diziyeCevir(sonuc);
    const gecerli = hesaplar.value.some(h => h.id === seciliHesap.value);
    if (!gecerli && hesaplar.value.length) {
      seciliHesap.value = hesaplar.value[0].id;
    }
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

watch(seciliHesap, yeni => seciliHesabiYaz(yeni));

onMounted(hesaplariYukle);

const sekmeAktif = ad => route.name === ad;
const sekmeyeGit = ad => router.push({ name: ad, params: { accountId: accountId.value } });
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="flex flex-col gap-3 px-6 pt-4 border-b border-n-weak">
      <div class="flex items-center gap-4">
        <h1 class="text-lg font-medium text-n-slate-12">{{ baslik }}</h1>
        <select
          v-if="hesapGerekli && hesaplar.length"
          v-model="seciliHesap"
          class="!mb-0 !w-auto min-w-[240px]"
        >
          <option v-for="h in hesaplar" :key="h.id" :value="h.id">
            {{ h.name }} · {{ h.currency }}
          </option>
        </select>
        <span class="grow" />
        <slot name="eylemler" />
      </div>

      <nav v-if="sekmeler.length" class="flex gap-1 -mb-px overflow-x-auto">
        <button
          v-for="s in sekmeler"
          :key="s.rota"
          type="button"
          class="px-3 py-2 text-sm whitespace-nowrap border-b-2 transition-colors"
          :class="
            sekmeAktif(s.rota)
              ? 'border-n-brand text-n-slate-12 font-medium'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="sekmeyeGit(s.rota)"
        >
          {{ s.ad }}
        </button>
      </nav>
    </header>

    <p v-if="hata" class="px-6 py-3 text-sm text-n-ruby-11">{{ hata }}</p>

    <div class="flex-1 overflow-auto">
      <!-- Alt sayfalar secili hesabi buradan alir -->
      <slot
        :hesap="seciliHesap"
        :hesapAyrinti="hesapAyrinti"
        :hesap-ayrinti="hesapAyrinti"
        :yukleniyor="yukleniyor"
      />
      <RouterView
        v-if="sekmeler.length"
        :hesap="seciliHesap"
        :hesap-ayrinti="hesapAyrinti"
      />
    </div>
  </div>
</template>
